import React from "react";
import PropTypes from "prop-types";
import { FileSpreadsheet } from "lucide-react";
import { useSelector } from "react-redux";

import Card from "../../../Card";
import { UIButton } from "../../../ui";
import messages from "../../../../assets/locale/messages";
import { clx } from "../../../../utils/Helpers";

import "./DocumentsSection.scss";

const DocumentsSection = ({
  documents,
  onSelect,
  onDownload,
  showPreview,
  asCard,
  stacked,
  className,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { registrationReq: t } = messages[lang];

  const handleDownload = (doc) => {
    onDownload?.(doc);
    if (doc?.url) {
      window.open(doc.url, "_blank", "noopener,noreferrer");
    }
  };

  const content =
    documents.length === 0 ? (
      <span className="company-registration-documents__empty">—</span>
    ) : (
      <div className="company-registration-documents__list">
        {documents.map((doc) => (
          <div
            key={doc.rowKey != null ? doc.rowKey : doc.url || doc.name}
            className="company-registration-documents__row"
          >
            <div className="company-registration-documents__meta">
              <span
                className="company-registration-documents__icon"
                aria-hidden="true"
              >
                <FileSpreadsheet size={24} strokeWidth={2} />
              </span>
              <div className="company-registration-documents__text">
                <div className="company-registration-documents__label">
                  {doc.name}
                </div>
                {doc.fileName ? (
                  <div className="company-registration-documents__filename">
                    {doc.fileName}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="company-registration-documents__actions">
              <UIButton
                type="button"
                variant="outline"
                size="medium"
                disabled={!doc.url}
                onClick={() => handleDownload(doc)}
              >
                {t.download}
              </UIButton>
              {showPreview ? (
                <UIButton
                  type="button"
                  variant="outline"
                  size="medium"
                  disabled={!doc.url}
                  onClick={() => onSelect(doc)}
                >
                  {t.preview}
                </UIButton>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    );

  if (!asCard) {
    return (
      <div
        className={clx(
          "company-registration-documents",
          stacked && "company-registration-documents--stacked",
          className,
        )}
      >
        {content}
      </div>
    );
  }

  return (
    <Card
      title={t.documents}
      classNames={{
        root: clx(
          "company-registration-documents",
          stacked && "company-registration-documents--stacked",
          className,
        ),
        body: "company-registration-documents__body",
      }}
    >
      {content}
    </Card>
  );
};

DocumentsSection.propTypes = {
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
      fileName: PropTypes.string,
      icon: PropTypes.node,
      rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  onSelect: PropTypes.func,
  onDownload: PropTypes.func,
  showPreview: PropTypes.bool,
  asCard: PropTypes.bool,
  stacked: PropTypes.bool,
  className: PropTypes.string,
};

DocumentsSection.defaultProps = {
  onSelect: () => {},
  onDownload: () => {},
  showPreview: true,
  asCard: true,
  stacked: false,
  className: "",
};

export default DocumentsSection;
